/**
 * exportContractHistoryCSV — generates a UTF-8 CSV file from the contract
 * ledger and triggers a browser download. Used by the in-game "Historique
 * des contrats" panel so the HST author can crunch balancing data outside
 * the app (Excel, R, Pandas, whatever).
 *
 * CSV columns mirror the ledger schema (see contractHistorySlice.js) with
 * a couple of derived helpers :
 *   - acceptedValue : the actual value the player committed to (revenue
 *                     for ponctuel, dailyRevenue*duration for régulier),
 *                     useful for portfolio analysis.
 *   - latencyDays   : acceptedOnDay - spawnDay (decision speed)
 *   - lifecycleDays : completedOnDay - acceptedOnDay (effective duration)
 *
 * Implementation note : we generate the file on the client side (no
 * backend round-trip) so the data never leaves the user's machine, which
 * matters for confidentiality.
 */

// Order matters — keeps the spreadsheet readable.
const COLUMNS = [
  'instanceId',
  'contractId',
  'name',
  'category',
  'frequency',
  'tierMin',
  'international',
  'requiresLicense',
  'requiresSpecialty',
  'clientName',
  'duration',
  'revenue',
  'dailyRevenue',
  'totalValue',
  'acceptedRevenue',
  'acceptedDailyRevenue',
  'acceptedValue',          // derived
  'argumentationMultiplier',
  'status',
  'spawnDay',
  'acceptedOnDay',
  'completedOnDay',
  'expiredOnDay',
  'cancelledOnDay',
  'displacedOnDay',
  'latencyDays',            // derived
  'lifecycleDays',          // derived
  'vehicleId',
  'driverId',
  'daysServed',
  'missedDeliveries',
];

function _escape(value) {
  if (value === null || value === undefined) return '';
  let s = String(value);
  // Protection injection de formule (Excel/LibreOffice) : un montant négatif reste numérique.
  if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+([.,]\d+)?$/.test(s)) s = `'${s}`;
  // RFC4180 — wrap in quotes if needed, double up any inner quotes.
  if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function _derive(row) {
  const acceptedValue = row.frequency === 'régulier' || row.frequency === 'regulier'
    ? (row.acceptedDailyRevenue || row.dailyRevenue || 0) * (row.duration || 0)
    : (row.acceptedRevenue || row.revenue || 0);
  const latency = (row.acceptedOnDay != null && row.spawnDay != null)
    ? row.acceptedOnDay - row.spawnDay : null;
  const lifecycle = (row.completedOnDay != null && row.acceptedOnDay != null)
    ? row.completedOnDay - row.acceptedOnDay : null;
  return { acceptedValue, latencyDays: latency, lifecycleDays: lifecycle };
}

export function buildContractHistoryCsv(ledger = []) {
  const header = COLUMNS.join(';');
  const lines = ledger.map(row => {
    const enriched = { ...row, ..._derive(row) };
    return COLUMNS.map(col => _escape(enriched[col])).join(';');
  });
  // BOM prefix so Excel picks up UTF-8 cleanly.
  return '\ufeff' + [header, ...lines].join('\n');
}

export function downloadContractHistoryCsv(ledger = [], filename) {
  const csv = buildContractHistoryCsv(ledger);
  const today = new Date().toISOString().slice(0, 10);
  const safeName = filename || `hst-contract-history-${today}.csv`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  if (typeof URL === 'undefined' || typeof document === 'undefined') return safeName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return safeName;
}

/* ── Catalogue export ────────────────────────────────────────────────── */
/**
 * Build a CSV from the CONTRACTS catalogue. This is the master template
 * list (NOT the runtime ledger) and is used to feed the "calendar of
 * unlock dates" feature the user wants in their Excel workbook.
 *
 * Columns are ordered to make balancing easy : id, name, category,
 * frequency, tierMin, requiredLicense/specialty, default revenue values,
 * planned `unlockOnDay` slot left blank for the user to fill in.
 */
const CATALOGUE_COLUMNS = [
  'id',
  'name',
  'category',
  'frequency',
  'tierMin',
  'requiresLicense',
  'requiresSpecialty',
  'international',
  'duration',
  'revenue_min',
  'revenue_max',
  'dailyRevenue_min',
  'dailyRevenue_max',
  'unlockOnDay',          // empty by default — to be filled by the author
  'comment',
];

export function buildContractCatalogueCsv(contracts = []) {
  const header = CATALOGUE_COLUMNS.join(';');
  const lines = contracts.map(c => {
    const row = {
      id: c.id,
      name: c.name,
      category: c.category || '',
      frequency: c.frequency || 'ponctuel',
      tierMin: c.tierMin || 1,
      requiresLicense: c.requiresLicense || '',
      requiresSpecialty: c.requiresSpecialty || '',
      international: c.international || '',
      duration: c.duration || '',
      // Support both naming conventions used in the catalogue
      // (`revenueMin/Max` on the main data file, `revenueRange` on older
      // ad-hoc fixtures used by some tests).
      revenue_min: c.revenueMin ?? (Array.isArray(c.revenueRange) ? c.revenueRange[0] : (c.revenue || '')),
      revenue_max: c.revenueMax ?? (Array.isArray(c.revenueRange) ? c.revenueRange[1] : (c.revenue || '')),
      dailyRevenue_min: c.dailyRevenueMin ?? (Array.isArray(c.dailyRevenueRange) ? c.dailyRevenueRange[0] : (c.dailyRevenue || '')),
      dailyRevenue_max: c.dailyRevenueMax ?? (Array.isArray(c.dailyRevenueRange) ? c.dailyRevenueRange[1] : (c.dailyRevenue || '')),
      unlockOnDay: c.unlockOnDay != null ? c.unlockOnDay : '',
      comment: c.condition || '',
    };
    return CATALOGUE_COLUMNS.map(col => _escape(row[col])).join(';');
  });
  return '\ufeff' + [header, ...lines].join('\n');
}

export function downloadContractCatalogueCsv(contracts = [], filename) {
  const csv = buildContractCatalogueCsv(contracts);
  const today = new Date().toISOString().slice(0, 10);
  const safeName = filename || `hst-contract-catalogue-${today}.csv`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  if (typeof URL === 'undefined' || typeof document === 'undefined') return safeName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return safeName;
}
